import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { Lieferant, LieferantCreate } from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import SortableHeader from './SortableHeader';
import { useTableSort } from '../utils/tableSort';
import { useToast } from './ToastProvider';
import { apiErrorMessage } from '../utils/apiError';

interface LieferantenTableProps {
  userRole: string;
}

const LieferantenTable: React.FC<LieferantenTableProps> = ({ userRole }) => {
  const [lieferanten, setLieferanten] = useState<Lieferant[]>([]);
  const { sort, order, onSort, sortRows } = useTableSort<'short_name' | 'name' | 'created_at'>(
    'name',
    'asc',
    ['created_at']
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toastError } = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editItem, setEditItem] = useState<Lieferant | null>(null);

  const [formName, setFormName] = useState('');
  const [formShortName, setFormShortName] = useState('');

  const isKalotte = userRole === 'KALOTTE';

  useEffect(() => {
    loadLieferanten();
  }, []);

  const loadLieferanten = async () => {
    try {
      setLoading(true);
      const items = await apiService.getLieferanten();
      setLieferanten(items);
      setError(null);
    } catch (err) {
      console.error('Error loading lieferanten:', err);
      setError('Fehler beim Laden der Lieferanten');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mode: 'create' | 'edit', item?: Lieferant) => {
    setDialogMode(mode);
    setEditItem(item || null);
    setFormName(item?.name || '');
    setFormShortName(item?.short_name || '');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditItem(null);
    setFormName('');
    setFormShortName('');
  };

  const handleSave = async () => {
    try {
      const data: LieferantCreate = {
        name: formName,
        short_name: formShortName,
      };

      if (dialogMode === 'create') {
        await apiService.createLieferant(data);
      } else if (editItem) {
        await apiService.updateLieferant(editItem.id, data);
      }

      handleCloseDialog();
      await loadLieferanten();
      setError(null);
    } catch (err: any) {
      console.error('Error saving lieferant:', err);
      toastError(apiErrorMessage(err, 'Fehler beim Speichern des Lieferanten'));
    }
  };

  const handleDelete = async (item: Lieferant) => {
    if (window.confirm(`Soll "${item.name} (${item.short_name})" wirklich gelöscht werden?`)) {
      try {
        await apiService.deleteLieferant(item.id);
        await loadLieferanten();
        setError(null);
      } catch (err: any) {
        console.error('Error deleting lieferant:', err);
        toastError(apiErrorMessage(err, 'Fehler beim Löschen des Lieferanten'));
      }
    }
  };

  const filteredItems = searchTerm
    ? lieferanten.filter((item) => {
        const term = searchTerm.toLowerCase();
        return (
          item.name.toLowerCase().includes(term) || item.short_name.toLowerCase().includes(term)
        );
      })
    : lieferanten;
  const visibleItems = sortRows(filteredItems, {
    short_name: (item) => item.short_name,
    name: (item) => item.name,
    created_at: (item) => item.created_at,
  });

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Lieferanten ({filteredItems.length}
          {searchTerm ? ` / ${lieferanten.length}` : ''})
        </Typography>
        {isKalotte && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('create')}
          >
            Neuer Lieferant
          </Button>
        )}
      </Box>

      <TextField
        size="small"
        placeholder="Suche nach Name, Kurzname..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2, width: { xs: '100%', sm: 400 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: searchTerm ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchTerm('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', py: 0.75 } }}>
            <TableHead>
              <TableRow>
                <SortableHeader column="short_name" label="Kurzname" sort={sort} order={order} onSort={onSort} />
                <SortableHeader column="name" label="Name" sort={sort} order={order} onSort={onSort} />
                <SortableHeader column="created_at" label="Erstellt am" sort={sort} order={order} onSort={onSort} />
                {isKalotte && <TableCell align="right">Aktionen</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell sx={{ fontWeight: 500 }}>{item.short_name}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{new Date(item.created_at).toLocaleDateString('de-DE')}</TableCell>
                  {isKalotte && (
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenDialog('edit', item)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(item)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Neuer Lieferant' : 'Lieferant bearbeiten'}
        </DialogTitle>
        <DialogContent>
          <TextField
            required
            fullWidth
            label="Name"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            margin="normal"
          />
          <TextField
            required
            fullWidth
            label="Kurzname"
            value={formShortName}
            onChange={(e) => setFormShortName(e.target.value)}
            margin="normal"
            helperText="Wird für die Chargen-Nummernbildung verwendet (z.B. S, A, B)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formName.trim() || !formShortName.trim()}
          >
            {dialogMode === 'create' ? 'Erstellen' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LieferantenTable;
